import { INavLink, INavLinkGroup as IBaseNavLinkGroup } from '@coherence-design-system/controls/lib/nav';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import React from 'react';
import { IHostAppState } from './Redux';
import { useLocation } from 'react-router-dom';
import { useDynamicReducer } from "@micro-frontend-react/employee-experience/lib/useDynamicReducer";
import { userRolesReducer, userRolesReducerName, userRolesInitialState } from './Redux/UserRoles/reducer';
import { IUserRolesAppState } from './Redux/index';
import { hasRoleAccess } from './Services/UserRolesService';
import { PAGE_ACCESS, FOLDER_ACCESS, hasSATAdmin, hasReportRole, hasIftSatAdminRole, hasAnyReportRole, hasIntakeFormToolAdminRole, hasSeveranceToolAdminRole, hasIntakeFormToolUserRole, hasNoRole } from './Services/UserRoleConfig';
import { IUserRoles } from '././Interfaces/IUserRoles';
import { Messages } from './Services/Constants';
import { useFeatureFlagList } from './Hooks/useFeatureFlagList';

export function setNav(onClickHandler: (ev: React.MouseEvent<HTMLElement>, item: INavLink) => void): IBaseNavLinkGroup[] {
  const { useSelector } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  const { folders } = useSelector((state: IHostAppState) => state.PagesReducer);
  const location = useLocation();
  const currentPath = location?.pathname;

  useDynamicReducer(userRolesReducerName, userRolesReducer, []);
  const userRolesState = useSelector((state: IUserRolesAppState) => state.dynamic?.[userRolesReducerName] || userRolesInitialState);
  const roles: IUserRoles | null = userRolesState.roles;

  // Read feature flag from hook
  const { isFeatureEnabled } = useFeatureFlagList();
  const intakeToolFeatureEnabled = !!isFeatureEnabled[__SECURITY_CONFIG__?.IFT_FEATURE_NAME];
  const satReportsFeatureEnabled = !!isFeatureEnabled[__SECURITY_CONFIG__?.SAT_REPORTS_FEATURE_NAME];
  const adminUploadFeatureEnabled = !!isFeatureEnabled[__SECURITY_CONFIG__?.ADMIN_UPLOAD_FEATURE_NAME];
  const packetGenerationFeatureEnabled = !!isFeatureEnabled[__SECURITY_CONFIG__?.PACKET_GENERATION_FEATURE_NAME];

  const noRoleNavConfig = [
    {
      key: 'Main',
      links: [] as INavLink[],
    }
  ];

  if (!roles) {
    return noRoleNavConfig;
  }

  let keyIndex = 1;
  const links: INavLink[] = [];

  const PacketGenerationNavItem: INavLink = {
    key: (keyIndex++).toString(),
    name: 'Home',
    href: '/PacketGeneration',
    icon: 'Home',
    onClick: onClickHandler,
    isSelected: currentPath === '/'
  };

  const securityNavItem: INavLink = {
    key: (keyIndex++).toString(),
    name: 'Security',
    href: '/Security',
    icon: 'Lock',
    onClick: onClickHandler,
    isSelected: currentPath === '/Security'
  };

  // Sort folders by the order defined in FOLDER_ACCESS
  // Filter out IFT folder if feature flag is disabled
  const orderedFolders = folders
    .filter(folder => FOLDER_ACCESS[folder.name])
    .filter(folder => {
      // Hide IFT folder when feature flag is disabled
      if (folder.name.toLowerCase() === Messages.IFT.toLowerCase() && !intakeToolFeatureEnabled) {
        return false;
      }
      if (folder.name.toLowerCase() === Messages.SatReports.toLowerCase() && !satReportsFeatureEnabled) {
        return false;
      }
      if (folder.name.toLowerCase() === Messages.AdminUpload.toLowerCase() && !adminUploadFeatureEnabled) {
        return false;
      }
      if (folder.name.toLowerCase() === Messages.PacketGeneration.toLowerCase() && !packetGenerationFeatureEnabled) {
        return false;
      }
      return true;
    })
    .sort((a, b) => (FOLDER_ACCESS[a.name].order ) > (FOLDER_ACCESS[b.name].order) ? 1 : -1);

  orderedFolders.forEach(folder => {
    if (!links.find(link => link.name === folder.name) && hasRoleAccess(folder.name, roles, FOLDER_ACCESS)) {
      const allowedPages = folder.pages.filter(page => {
          const hasAccess = hasRoleAccess(page.route, roles, PAGE_ACCESS)
          //check has report procedure access roles
          if (hasAccess && folder.name === Messages.SatReports) {
            return hasSATAdmin(roles) || hasReportRole(roles, page.name as keyof IUserRoles);
          }
        return hasAccess;
      });

      if (allowedPages.length > 0) {
      links.push(
        {
          key: (keyIndex++).toString(),
          name: folder.name,
          icon: folder.icon,
          links: allowedPages.map(
            page => {
              return {
                key: (keyIndex * 10 + parseInt(page.key)).toString(),
                name: page.navName,
                href: page.route,
                icon: page.icon,
                onClick: onClickHandler,
                isSelected: currentPath === page.route
              }
            }
          )
        }
      )
    }
    }
    });

  const satAdminNavConfig = [
    {
      key: 'Main',
      links: [
        ...(packetGenerationFeatureEnabled ? [PacketGenerationNavItem] : []),
        securityNavItem,
        ...links,
      ]
    }
  ];

  const reportUserNavConfig = [
    {
      key: 'Main',
      links: [...links]
    }
  ];

  const intakeFormToolAdminNavConfig = [
    {
      key: 'Main',
      links: [securityNavItem, ...links]
    }
  ];

  const iftSatAdminNavConfig = [
    {
      key: 'Main',
      links: [securityNavItem,...links]
    }
  ];

  // Combine links for users having both ReportUser and IftSatAdmin roles
  const reportUserAndIftSatAdminNavConfig = [
    {
      key: 'Main',
      links: [securityNavItem,...links]
    }
  ];
  
  const severanceToolAdminNavConfig = [
    {
      key: 'Main',
      links: [securityNavItem,...links]
    }
  ];

  // Intake form tool user role nav config without security page access
  const intakeFormToolUserNavConfig = [
    {
      key: 'Main',
      links: [...links]
    }
  ];

  // NavConfig Role check based on priority chain
  if (hasSATAdmin(roles)) {
    return satAdminNavConfig;
  } else if (hasAnyReportRole(roles) && hasIftSatAdminRole(roles)) {
    return reportUserAndIftSatAdminNavConfig;
  } else if (hasAnyReportRole(roles)) {
    return reportUserNavConfig;
  } else if (hasIftSatAdminRole(roles)) {
    return iftSatAdminNavConfig;
  } else if (hasIntakeFormToolAdminRole(roles)) {
     return intakeFormToolAdminNavConfig;
  } else if (hasSeveranceToolAdminRole(roles)) {
    return severanceToolAdminNavConfig;
  } else if (hasIntakeFormToolUserRole(roles)) {
    return intakeFormToolUserNavConfig;
  } else if (hasNoRole(roles)) {
    return noRoleNavConfig;
  }
  return noRoleNavConfig;
}
